/**
 * InitiativeCard — collapsible card for one strategic_initiative.
 *
 * Phase 44.5.
 */

import { useState } from "react";

export default function InitiativeCard({
  initiative,
  capabilityNames = [],
  onChange,
  onRemove,
}) {
  const [expanded, setExpanded] = useState(false);

  const update = (key, value) =>
    onChange({ ...initiative, [key]: value });

  const toggleCapability = (name) => {
    const current = initiative?.capabilities_required || [];
    // Compare on trimmed form so stray whitespace on either side doesn't
    // prevent an unchecked → checked toggle. We still STORE the name the
    // user selected (from capabilityNames, which is pre-trimmed) so the
    // stored value is normalised going forward.
    const targetTrim = name.trim();
    const hit = current.find((c) => (c || "").trim() === targetTrim);
    const next = hit
      ? current.filter((c) => (c || "").trim() !== targetTrim)
      : [...current, targetTrim];
    update("capabilities_required", next);
  };

  return (
    <div className="initiative-card">
      <div className="initiative-card__header">
        <button
          type="button"
          className="capability-card__toggle"
          onClick={() => setExpanded((x) => !x)}
          aria-expanded={expanded}
        >
          <span className={`capability-card__chevron ${expanded ? "capability-card__chevron--open" : ""}`}>
            ▸
          </span>
          <span className="capability-card__name">
            {initiative?.name?.trim() || <em className="muted">Untitled initiative</em>}
          </span>
          <span className="initiative-card__caprefs">
            {(initiative?.capabilities_required || []).length} capabilities
          </span>
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-sm capability-card__delete"
          onClick={() => {
            if (window.confirm(`Delete initiative "${initiative?.name || "Untitled"}"?`)) {
              onRemove();
            }
          }}
        >
          Delete
        </button>
      </div>

      {expanded && (
        <div className="capability-card__body">
          <label className="setup-field">
            <span className="setup-field__label">Name</span>
            <input
              type="text"
              className="setup-field__input"
              value={initiative?.name || ""}
              onChange={(e) => update("name", e.target.value)}
              maxLength={200}
            />
          </label>

          <label className="setup-field">
            <span className="setup-field__label">Description</span>
            <textarea
              className="setup-field__input"
              value={initiative?.description || ""}
              onChange={(e) => update("description", e.target.value)}
              rows={3}
              maxLength={2000}
            />
          </label>

          <div className="setup-field">
            <span className="setup-field__label">Capabilities required</span>
            <p className="setup-field__help">
              Names must match capabilities defined under the pillars. Names that
              don&apos;t resolve surface as validator warnings — the backend
              doesn&apos;t hard-fail them.
            </p>
            {capabilityNames.length === 0 ? (
              <p className="initiative-card__empty">
                No capabilities defined yet. Add capabilities under the pillar
                tabs first.
              </p>
            ) : (
              <div className="initiative-card__caplist">
                {capabilityNames.map((name) => {
                  const targetTrim = name.trim();
                  const checked = (initiative?.capabilities_required || []).some(
                    (c) => (c || "").trim() === targetTrim
                  );
                  return (
                    <label key={name} className="initiative-card__capoption">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleCapability(name)}
                      />
                      <span>{name}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
